import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@chakra-ui/react';
import { Upload } from 'lucide-react';
import { importRhinoOptions, readRhinoOptionsFile, RHINO_IMPORT_PRESET_NAME } from '../../utils/rhinoOptions';
import { notify } from '../../utils/notify';

/**
 * "Import Rhino Options..." button for the Keyboard Shortcuts editor
 * footer. Reads a McNeel Rhino Options .ini export (Options → Export),
 * resolves each command alias and keyboard-page macro to the equivalent
 * app command and applies the bindings as user shortcuts (persisted by the
 * command store).
 */
export function RhinoOptionsImportButton() {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportFile = async (file: File) => {
        try {
            const summary = importRhinoOptions(await readRhinoOptionsFile(file));
            const aliasCount = summary.applied.filter((a) => a.source === 'alias').length;
            const keyCount = summary.applied.length - aliasCount;
            if (summary.totalAliases + summary.totalKeyboardMacros === 0) {
                notify('No aliases or keyboard macros found in the Rhino options file.', 'warning');
                return;
            }
            const parts = [`${aliasCount} alias(es) applied`, `${keyCount} keyboard shortcut(s) applied`];
            if (summary.presetAliases > 0) {
                parts.push(
                    `${summary.presetAliases} command-line alias(es) registered ` +
                        `(${summary.presetCreated ? 'created' : 'updated'} the "${RHINO_IMPORT_PRESET_NAME}" preset)`
                );
            }
            if (summary.settingsApplied.length > 0) {
                parts.push(`${summary.settingsApplied.length} setting(s) applied`);
            }
            if (summary.skipped.length > 0) parts.push(`${summary.skipped.length} skipped`);
            notify(
                `Rhino options imported: ${parts.join(', ')}.`,
                summary.applied.length > 0 ? 'success' : 'warning'
            );
            // Per-entry details live in the console: the toast only carries totals.
            const unmapped = summary.skipped.filter((s) => s.reason === 'unmapped-command');
            if (unmapped.length > 0) {
                console.warn(
                    '[RhinoOptions] Rhino commands without an app equivalent:',
                    unmapped.map((s) => `${s.shortcut} → ${s.rhinoCommand || '(no command)'}`)
                );
            }
            const unmappedKeys = summary.skipped.filter((s) => s.reason === 'unmapped-key');
            if (unmappedKeys.length > 0) {
                console.warn(
                    '[RhinoOptions] Keyboard macros in key slots without a known key binding:',
                    unmappedKeys.length
                );
            }
            for (const collision of summary.collisions) {
                console.warn(
                    `[RhinoOptions] Shortcut '${collision.shortcut}' also remains assigned to '${collision.commandLabel}'.`
                );
            }
            if (summary.settingsApplied.length > 0) {
                console.info(`[RhinoOptions] Settings applied: ${summary.settingsApplied.join(', ')}.`);
            }
            if (summary.ignoredSectionNames.length > 0) {
                console.info(
                    `[RhinoOptions] ${summary.ignoredSectionNames.length} section(s) left to the app's own settings: ` +
                        summary.ignoredSectionNames.slice(0, 15).join(', ') +
                        (summary.ignoredSectionNames.length > 15 ? ', …' : '')
                );
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error.';
            notify(`Rhino options import failed: ${message}`, 'error');
        }
    };

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                leftIcon={<Upload size={14} />}
                flexShrink={0}
                whiteSpace="nowrap"
                onClick={() => fileInputRef.current?.click()}
            >
                {t('dialogs.ui.importRhinoOptions')}
            </Button>
            <input
                ref={fileInputRef}
                type="file"
                accept=".ini,.txt,.cfg,text/plain"
                className="hidden-file-input"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImportFile(file);
                    e.target.value = '';
                }}
            />
        </>
    );
}
